import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useModulePager } from "Hooks";
import { SpaceType, VenueSearchResult, UserVenuesClient, SavedSearchRequest, GeoSearchRequest, ReservationMethod, CurrentLocationSearchRequest, FullTextSearchRequest } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { getSearchUrl } from "Utils/getSearchUrl";
import LoadingPlaceholder from "./LoadingPlaceholder";
import { SearchContext } from "../../Main/SearchContextProvider";
import ModulePager from "../../ModulePager";
import ModulePagerControls from "../../ModulePager/ModulePagerControls";
import { VenueCard } from "../../VenueCard";
import UserContext from "../../../UserContext";

interface RecentTabProps {
   onVenueCardClick: (e: React.SyntheticEvent, venue: VenueSearchResult) => void;
   controlsElement: HTMLElement | null;
}

const RecentTab: React.FC<RecentTabProps> = ({ onVenueCardClick, controlsElement }) => {
   const userVenuesClient = useApiClient(UserVenuesClient);
   const { enterpriseUser } = useContext(UserContext);
   const [homeBase, setHomeBase] = useState<SavedSearchRequest>();
   const [recentVenues, setRecentVenues] = useState<VenueSearchResult[]>([]);
   const searchManager = useContext(SearchContext);
   const [isLoading, setLoading] = useState(true);

   const recentVenuesPager = useModulePager({
      itemCount: recentVenues.length,
      itemWidth: 256,
      gapWidth: 32
   });

   const loadRecentVenues = async (abortSignal: AbortSignal) => {
      try {
         setLoading(true);
         const result = await userVenuesClient.getRecent(abortSignal);
         setRecentVenues(result);
         if (!result.length && enterpriseUser.homeBaseSearchId) {
            setHomeBase(await searchManager.getSavedSearchCached(enterpriseUser.homeBaseSearchId));
         }
      }
      finally {
         setLoading(false);
      }
   }

   const getSpaceTypeSearchLink = (spaceType: SpaceType) => {
      const request = homeBase?.request;
      const spaceTypeRequest = request instanceof GeoSearchRequest
         //main case
         ? new GeoSearchRequest({
            ...new GeoSearchRequest(),
            address: request.address,
            reservationMethod: ReservationMethod.Hourly,
            spaceTypes: spaceType
         })
         //fallback cases
         : request instanceof FullTextSearchRequest
            ? new GeoSearchRequest({
               ...new GeoSearchRequest(),
               address: request.fullTextQuery,
               reservationMethod: ReservationMethod.Hourly,
               spaceTypes: spaceType
            })
            : new CurrentLocationSearchRequest({
               ...new CurrentLocationSearchRequest(),
               reservationMethod: ReservationMethod.Hourly,
               spaceTypes: spaceType
            });
      return getSearchUrl(spaceTypeRequest, true);
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadRecentVenues(abortController.signal);
      return function cleanup() {
         abortController.abort();
      }
   }, []);

   useEffect(() => {
      recentVenuesPager.reset();
   }, [recentVenues])

   return <div className="tap-pane active">
      {
         controlsElement != null
            ? createPortal(<ModulePagerControls pager={recentVenuesPager} />, controlsElement)
            : null
      }

      {
         isLoading
            ? <LoadingPlaceholder />
            : !recentVenues.length
               ? <div className="explore-recent-null-state">
                  <div className="explore-recent-null-state-text">You don't have any recent activity yet. Start by exploring locations nearby.</div>
                  <div className="explore-recent-null-state-tiles">
                     <a href={getSpaceTypeSearchLink(SpaceType.Office)} className="explore-recent-null-state-tile explore-recent-null-state-tile-office">
                        <div className="explore-recent-null-state-tile-caption">Spaces to concentrate</div>
                        <div className="explore-recent-null-state-tile-text">Explore private offices nearby</div>
                     </a>
                     <a href={getSpaceTypeSearchLink(SpaceType.Meeting)} className="explore-recent-null-state-tile explore-recent-null-state-tile-meeting">
                        <div className="explore-recent-null-state-tile-caption">Spaces to collaborate</div>
                        <div className="explore-recent-null-state-tile-text">Explore meeting rooms nearby</div>
                     </a>
                     <a href={getSpaceTypeSearchLink(SpaceType.Desk)} className="explore-recent-null-state-tile explore-recent-null-state-tile-desk">
                        <div className="explore-recent-null-state-tile-caption">Spaces to cowork</div>
                        <div className="explore-recent-null-state-tile-text">Explore desks nearby</div>
                     </a>
                  </div>
               </div>
               : <ModulePager
                  pager={recentVenuesPager}
                  items={recentVenues}
                  renderItem={(venue, style) =>
                     <VenueCard
                        key={venue.venueId}
                        venue={venue}
                        onClick={onVenueCardClick}
                        style={style}
                     />}
               />
      }
   </div>;
}

export default RecentTab;